class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;
    this.closingTimeout;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

    this.openHandler = this.open.bind(this);
    this.closeWithClosingClassHandler = this.closeWithClosingClass.bind(this);

    if (!this.dataset.ignoreHover) {
      this.isAbleToUseMouse = matchMedia('(hover: hover)').matches;

      this.debouncedOnChange = debounce((event) => {
        this.isAbleToUseMouse = matchMedia('(hover: hover)').matches;
        this.initHoverDetails();
      }, 300);

      window.addEventListener('resize', this.debouncedOnChange.bind(this));

      this.initHoverDetails();
    }

    this.initClickSummary();
  }

  initHoverDetails() {
    if (this.isAbleToUseMouse) {
      Shopify.addListener(this.mainDetailsToggle, 'mouseenter', this.openHandler);
      Shopify.addListener(this.mainDetailsToggle, 'mouseleave', this.closeWithClosingClassHandler);
    } else {
      this.mainDetailsToggle.removeEventListener('mouseenter', this.openHandler);
      this.mainDetailsToggle.removeEventListener('mouseleave', this.closeWithClosingClassHandler);
    }
  }

  initClickSummary() {
    this.mainDetailsToggle.querySelector('summary').addEventListener('click', (e) => {
      e.preventDefault();
      if (this.mainDetailsToggle.hasAttribute('open')) {
        this.closeWithClosingClass();
      } else {
        this.open();
      }
    });
  }

  closeWithClosingClass() {
    if (this.classList.contains('ignore-close')) return;
    this.mainDetailsToggle.classList.add('closing');
    if (this.closingTimeout) {
      clearTimeout(this.closingTimeout);
    }
    this.closingTimeout = setTimeout(() => {
      this.close();
      this.mainDetailsToggle.classList.remove('closing');
    }, 300);
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    this.closeWithClosingClass();
    this.mainDetailsToggle.querySelector('summary').focus();
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.closeWithClosingClass();
    })
  }

  playAnimation() {
    if (!this.animations) this.animations = this.content.getAnimations();
    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach(animation => animation.play());
    } else {
      this.animations.forEach(animation => animation.cancel());
    }
  }

  open() {
    if (this.closingTimeout) {
      clearTimeout(this.closingTimeout);
      this.closingTimeout = null;
    }
    this.mainDetailsToggle.classList.remove('closing');
    this.mainDetailsToggle.setAttribute('open', true);
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true);
    this.animations = null;
    this.playAnimation();
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

let hoverSupport = matchMedia('(hover: hover)').matches;
window.addEventListener('resize', () => {
  hoverSupport = matchMedia('(hover: hover)').matches;
});

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header');
    this.revealFrame = null;
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
    this.querySelectorAll(':scope > details > .header__menu-item--link').forEach(element => element.addEventListener('click', this.onClickSummary));
  }

  get isMegaMenu() {
    return this.content?.classList.contains('mega-menu__content');
  }

  cancelReveal() {
    if (this.revealFrame) {
      cancelAnimationFrame(this.revealFrame);
      this.revealFrame = null;
    }
  }

  open() {
    if (this.closingTimeout) {
      clearTimeout(this.closingTimeout);
      this.closingTimeout = null;
    }
    this.cancelReveal();
    this.mainDetailsToggle.classList.remove('closing');
    this.mainDetailsToggle.setAttribute('open', true);
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true);

    if (!this.isMegaMenu) {
      this.animations = null;
      this.playAnimation();
      return;
    }

    this.mainDetailsToggle.classList.remove('is-revealed');
    void this.mainDetailsToggle.offsetWidth;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.mainDetailsToggle.classList.add('is-revealed');
      return;
    }
    this.revealFrame = requestAnimationFrame(() => {
      this.revealFrame = requestAnimationFrame(() => {
        this.revealFrame = null;
        this.mainDetailsToggle.classList.add('is-revealed');
      });
    });
  }

  closeWithClosingClass() {
    if (this.classList.contains('ignore-close')) return;
    this.cancelReveal();
    this.mainDetailsToggle.classList.add('closing');
    this.mainDetailsToggle.classList.remove('is-revealed');
    if (this.closingTimeout) {
      clearTimeout(this.closingTimeout);
    }
    const closeMs = this.isMegaMenu ? 600 : 300;
    this.closingTimeout = setTimeout(() => {
      this.close();
      this.mainDetailsToggle.classList.remove('closing');
    }, closeMs);
  }

  close() {
    this.cancelReveal();
    this.mainDetailsToggle.classList.remove('is-revealed');
    super.close();
  }

  onClickSummary(event) {
    if (hoverSupport) {
      event.preventDefault();
      window.location = event.currentTarget.dataset.href;
    }
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;
    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty('--header-bottom-position-desktop', `${Math.floor(this.header.getBoundingClientRect().bottom)}px`);
  }
}

customElements.define('header-menu', HeaderMenu);

class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    // this.overlay = this.querySelector('.details-modal-overlay');
    this.productItem = this.querySelectorAll('.predictive-search__suggestions-item') || ''
    this.topAppContainer = ''

    this.detailsContainer.addEventListener(
      'keyup',
      (event) => (event.code) ? event.code.toUpperCase() === 'ESCAPE' && this.close() : ''
    );
    this.summaryToggle.addEventListener(
      'click',
      this.onSummaryClick.bind(this)
    );
    this.querySelector('.modal__close-button')?.addEventListener(
      'click',
      this.close.bind(this)
    );
    this.summaryToggle.setAttribute('role', 'button');
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();

    const isModalOpen = event.target.closest('details').hasAttribute('open');

    // 同步查找顶部容器，不阻塞弹窗打开
    this.topAppContainer = document.querySelector('#epb_container') || null;

    if (isModalOpen) {
      this.close();
    } else {
      if (this.topAppContainer) {
        this.topAppContainer.style.display = 'none';
      }
      this.open(event);
    }
  }

  open(event) {
    if (event) {
      event.target.closest('details').setAttribute('open', true);
      //product item动画
      if (this.productItem) {
        this.productItem.forEach((item) => {
          item.classList.add('predictive-search__suggestions-item-animation');
        })
      }
    }

    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);

    // 添加防御性检查
    const focusElement = this.detailsContainer.querySelector('[tabindex="-1"]');
    const fallbackElement = this.detailsContainer.querySelector('input:not([type="hidden"])');
    if (focusElement || fallbackElement) {
      trapFocus(focusElement, fallbackElement);
    } else {
      // 如果没有合适的元素，可以使用容器本身
      this.detailsContainer.setAttribute('tabindex', '-1');
      trapFocus(this.detailsContainer);
    }

  }

  close(focusToggle = true) {
    // 显示top滚动插件
    this.topAppContainer ? this.topAppContainer.style.display = 'block' : '';
    //product动画
    if (this.productItem) {
      this.productItem.forEach((item) => {
        item.classList.remove('predictive-search__suggestions-item-animation');
      })
    }
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
  }


}

customElements.define('details-modal', DetailsModal);